<!-- <script lang="ts">
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("SidebarSections.vue");
</script> -->

<script setup lang="ts">
import { ref, watch } from "vue";
import AddSection from "./sections/AddSection.vue";
import SectionsList from "./sections/SectionsList.vue";
import EditBlock from "./sections/block/EditBlock.vue";
import { useActions, useGetters } from "@/store/vuex-composition-mappers";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useRoute } from "vue-router";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import { useAsyncState } from "@vueuse/core";
import { pageToolsService } from "@/apps/page/services/page-tools.service";
import type { PageSection } from "@/apps/page/page.interfaces";
import { PAGE_SECTION_KINDS } from "@/apps/page/services/page.service";
import EditTool from "@/apps/page/project/sidebar/sections/tool/EditTool.vue";

// @ts-expect-error - TODO BF: Fix types
const { removeBlockSection, changeBlockSection } = useActions("page", [
    "removeBlockSection",
    "changeBlockSection",
]);
// @ts-expect-error - TODO BF: Fix types
const { saveSnapshot } = useActions("page/projects", ["saveSnapshot"]);
const { backupData, setBackupValue } = useBackupData<PageSection>({} as PageSection);
// @ts-expect-error - TODO BF: Fix types
const { getSectionById } = useGetters("page", ["getSectionById"]);

const SIDEBAR_MODES = {
    list: "list",
    addSection: "addSection",
    createBlock: "createBlock",
    editSection: "editSection",
    editBlock: "editBlock",
    editTool: "editTool",
};

const currentSection = ref<PageSection | null>(null);

const route = useRoute();
const mode = ref(SIDEBAR_MODES.list);
const { cleanQueryParams } = useCleanQueryParams();

const edit = (section: PageSection) => {
    setBackupValue(section);
    currentSection.value = section;
    if (!currentSection.value.styleValues) {
        currentSection.value.styleValues = {};
    }
    mode.value = SIDEBAR_MODES.editBlock;
};

const { state: toolProjects } = useAsyncState(
    () => pageToolsService.getAllUserToolProjects(),
    // @ts-expect-error - TODO BF: fix type
    {},
    {
        immediate: true,
    }
);

watch(
    () => route.query.open,
    (value) => {
        switch (value) {
            case "addSection":
                mode.value = SIDEBAR_MODES.addSection;
                break;
            case "editBlock":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id));
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "editSection":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id));
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "createBlock":
                if (!route.query.id) {
                    return;
                }

                currentSection.value = getSectionById.value(+route.query.id);
                mode.value = SIDEBAR_MODES.createBlock;
                setTimeout(() => {
                    cleanQueryParams(
                        typeof route.query.id === "string" ? ["open", "id"] : ["open"]
                    );
                });
                break;
            default:
                break;
        }
    },
    { immediate: true }
);

const goBack = () => {
    mode.value = SIDEBAR_MODES.list;
    cleanQueryParams(["open"]);
};

const clearBlock = () => {
    currentSection.value = null;
    mode.value = SIDEBAR_MODES.list;
};

const saveBlock = async () => {
    clearBlock();
    await saveSnapshot();
};

const cancelBlock = async () => {
    if (mode.value === SIDEBAR_MODES.createBlock) {
        if (!currentSection.value) return;
        removeBlockSection(currentSection.value.id);
    } else if (mode.value === SIDEBAR_MODES.editBlock) {
        if (!backupData.value) return;
        changeBlockSection(backupData.value as PageSection);
    } else {
        throw new Error("Invalid mode");
    }

    clearBlock();
    await saveSnapshot();
};
</script>

<template>
    <SectionsList
        v-if="mode === SIDEBAR_MODES.list"
        @addSection="mode = SIDEBAR_MODES.addSection"
        @editSection="edit"
    />
    <AddSection
        v-if="mode === SIDEBAR_MODES.addSection"
        :toolProjects="toolProjects as Record<string, any>"
        @back="goBack"
    />
    <EditBlock
        :key="currentSection?.id"
        v-if="
            (mode === SIDEBAR_MODES.createBlock || mode === SIDEBAR_MODES.editBlock) &&
            currentSection !== null &&
            currentSection.kind === PAGE_SECTION_KINDS.BLOCK
        "
        :section="currentSection"
        @saveBlock="saveBlock"
        @cancelBlock="cancelBlock"
    />
    <EditTool
        :key="currentSection?.id"
        v-if="
            (mode === SIDEBAR_MODES.createBlock || mode === SIDEBAR_MODES.editBlock) &&
            currentSection !== null &&
            currentSection.kind === PAGE_SECTION_KINDS.TOOL
        "
        :section="currentSection"
        :backupData="backupData"
        @save="saveBlock"
        @quit="cancelBlock"
    />
</template>
